<template>
	<div class="d-inline-flex align-items-center">
		<template v-if="!showLanguageSelect">
			<v-tab :class="['rounded-pill','border','px-3','py-1', hasRequestedTranslator ? 'bg-secondary text-white' : 'bg-light']" @click="openLanguageSelect">
				<v-tooltip activator="parent" location="bottom" class="custom-tooltip">
					{{ $t('message.AgendaCard.tooltip_translator') }}
				</v-tooltip>
				<template v-if="hasRequestedTranslator && selectedLanguageOption">
					<component v-if="selectedLanguageOption.icon" :is="selectedLanguageOption.icon" class="me-2" style="width:20px;height:20px" />
					{{ $t('message.AgendaCard.translator_requested', { language: selectedLanguageOption.label }) }}
				</template>
				<template v-else>
					<i class="mdi mdi-earth me-2" aria-hidden="true"></i>
					{{ $t('message.AgendaCard.need_translator') }}
				</template>
			</v-tab>
			<button
				v-if="hasRequestedTranslator"
				type="button"
				class="btn btn-sm btn-link text-muted ms-2 p-0 align-self-center"
				title="{{ $t('message.AgendaCard.clear_translator') }}"
				@click.stop="clearTranslator"
			>
				<i class="bi bi-x-circle" aria-hidden="true"></i>
			</button>
		</template>
		<template v-else>
			<div class="d-inline-block">
				<v-select
					class="v-select-pill w-auto"
					style="width:auto; min-width: unset;"
					v-model="selectedLanguage"
					:items="selectItems"
					item-title="label"
					item-value="code"
					variant="outlined"
					rounded="pill"
					density="compact"
					hide-details
					@update:modelValue="onLanguageChange"
				>
					<template #item="{ props, item }">
						<v-list-item v-bind="props">
							<template #prepend>
								<component v-if="item.raw.icon" :is="item.raw.icon" class="me-2" style="width:20px;height:20px" />
							</template>
							<template #title>
								{{ item.raw.label }}
							</template>
						</v-list-item>
					</template>
					<template #selection="{ item }">
						<div class="d-flex align-items-center">
							<component v-if="item.raw.icon" :is="item.raw.icon" class="me-2" style="width:20px;height:20px" />
							<span>{{ item.raw.label }}</span>
						</div>
					</template>
				</v-select>
			</div>
		</template>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAgendaStore } from '@/stores/parent/agenda';
import { SUPPORTED_LANGUAGES } from '@/composables/useLanguage.js';
import enFlag from '@/assets/icons/flags/usa.svg';
import esFlag from '@/assets/icons/flags/es.svg';
import frFlag from '@/assets/icons/flags/fr.svg';
import deFlag from '@/assets/icons/flags/de.svg';
import arFlag from '@/assets/icons/flags/ar.svg';
import zhFlag from '@/assets/icons/flags/cn.svg';
import jaFlag from '@/assets/icons/flags/jp.svg';
import bnFlag from '@/assets/icons/flags/bd.svg';
import krFlag from '@/assets/icons/flags/kr.svg';

const props = defineProps({
	conference: { type: Object, required: true },
});

const { t } = useI18n();
const agendaStore = useAgendaStore();

const showLanguageSelect = ref(false);
const selectedLanguage = ref(null);
const hasRequestedTranslator = ref(false);
const initializedFromSchedule = ref(false);

const codeToIcon = {
	en: enFlag,
	es: esFlag,
	fr: frFlag,
	de: deFlag,
	ar: arFlag,
	zh: zhFlag,
	ja: jaFlag,
	bn: bnFlag,
	ko: krFlag,
};

const languages = computed(() => {
	const allowedCodes = Array.isArray(props.conference?.translator_languages)
		? props.conference.translator_languages
		: [];
	return SUPPORTED_LANGUAGES
		.filter(l => allowedCodes.includes(l.code))
		.map(l => ({ code: l.code, label: l.nativeName || l.name, icon: codeToIcon[l.code] }));
});

const selectItems = computed(() => {
	const langs = languages.value;
	if (langs.length <= 1) return langs;
	return [{ code: null, label: t('message.AgendaCard.choose_language'), icon: null }, ...langs];
});

const selectedLanguageOption = computed(() => {
	if (!selectedLanguage.value) return null;
	return languages.value.find(lang => lang.code === selectedLanguage.value) || null;
});

watch(
	languages,
	async (langs) => {
		// Auto-select and persist if only one language is supported
		if (props.conference?.request_translator && langs.length === 1 && !selectedLanguage.value) {
			selectedLanguage.value = langs[0].code;
			hasRequestedTranslator.value = true;
			showLanguageSelect.value = false;
			if (props.conference?.conference_id) {
				try {
					await agendaStore.requestTranslator(props.conference.conference_id, langs[0].code);
				} catch (e) {}
			}
		}
	},
	{ immediate: true }
);

async function openLanguageSelect() {
	const langs = languages.value;
	if (langs.length === 1) {
		const code = langs[0].code;
		if (selectedLanguage.value !== code) {
			selectedLanguage.value = code;
		}
		hasRequestedTranslator.value = true;
		showLanguageSelect.value = false;
		if (props.conference?.conference_id) {
			try {
				await agendaStore.requestTranslator(props.conference.conference_id, code);
			} catch (e) {}
		}
		return;
	}
	if (langs.length > 1) {
		showLanguageSelect.value = true;
	}
}

function initTranslatorFromSchedule() {
	if (initializedFromSchedule.value) return;
	const appointments = Array.isArray(agendaStore.agenda) ? agendaStore.agenda : [];
	const withTranslator = appointments
		.filter(a => a && a.conference_id === props.conference?.conference_id)
		.find(a => a.translator_language);
	if (withTranslator && withTranslator.translator_language) {
		selectedLanguage.value = withTranslator.translator_language;
		hasRequestedTranslator.value = true;
		showLanguageSelect.value = false;
		initializedFromSchedule.value = true;
	}
}

onMounted(() => {
	initTranslatorFromSchedule();
});

watch(() => agendaStore.agenda, () => {
	initTranslatorFromSchedule();
});

async function onLanguageChange(newLocale) {
	selectedLanguage.value = newLocale;
	hasRequestedTranslator.value = true;
	showLanguageSelect.value = false;
	try {
		if (props.conference?.conference_id) {
			await agendaStore.requestTranslator(props.conference.conference_id, newLocale || null);
		}
	} catch (e) {
		// silently ignore
	}
}

async function clearTranslator() {
	selectedLanguage.value = null;
	hasRequestedTranslator.value = false;
	try {
		if (props.conference?.conference_id) {
			await agendaStore.requestTranslator(props.conference.conference_id, null);
		}
	} catch (e) {
		// silently ignore
	}
}
</script>

<style scoped>
.v-select-pill {
	display: inline-flex;
	width: auto;
	max-width: none;
}
.v-select-pill :deep(.v-field) {
	border-radius: 9999px;
	/* max-height: 30px; */
	border: 1px solid rgba(0,0,0,.125);
	width: auto;
}
.v-select-pill :deep(.v-input) { width: auto; min-width: 0; }
.v-select-pill :deep(.v-field__outline) {
	display: none;
}
.v-select-pill :deep(.v-select__selection-text) {
	display: flex;
	align-items: center;
}

.v-select-pill :deep(.v-field__clearable) {
	margin-right: 4px;
}

.v-select-pill :deep(.v-field__input),
.v-select-pill :deep(.v-field__append-inner),
.v-select-pill :deep(.v-field__prepend-inner) {
	padding-top: 0px;
	padding-bottom: 0px;
}
</style>


